import React, { useState, useEffect, useRef } from "react";
import confetti from "canvas-confetti";

const API = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
  ? "http://localhost:5000"
  : (process.env.REACT_APP_API_URL || "https://crickbet-backend-api.netlify.app");

// ── Market definitions ────────────────────────────────────────────────────────
const MARKETS = {
  next_ball: {
    label: "Next Ball Outcome",
    icon: "🎯",
    description: "Bet on the exact result of the next delivery",
    scope: "ball",
    outcomes: [
      { key: "dot",    label: "Dot Ball",     emoji: "⬛", color: "#666" },
      { key: "single", label: "1-3 Runs",     emoji: "1️⃣", color: "#3498db" },
      { key: "four",   label: "Boundary (4)", emoji: "🏏", color: "#f39c12" },
      { key: "six",    label: "SIX! (6)",     emoji: "🔥", color: "#e67e22" },
      { key: "wicket", label: "WICKET!",      emoji: "💥", color: "#e74c3c" },
      { key: "extra",  label: "Wide/No-Ball", emoji: "↩️", color: "#9b59b6" }
    ]
  },
  dismissal: {
    label: "Method of Dismissal",
    icon: "🏏",
    description: "How will the next wicket fall? (Only active if wicket falls)",
    scope: "ball",
    outcomes: [
      { key: "caught",  label: "Caught",   emoji: "🙌", color: "#e74c3c" },
      { key: "bowled",  label: "Bowled",   emoji: "💨", color: "#f39c12" },
      { key: "lbw",     label: "LBW",      emoji: "🦵", color: "#e67e22" },
      { key: "run_out", label: "Run Out",  emoji: "🏃", color: "#3498db" },
      { key: "stumped", label: "Stumped",  emoji: "⚡", color: "#9b59b6" }
    ]
  },
  over_runs: {
    label: "Total Runs in Over",
    icon: "📊",
    description: "Will this over produce more or less than 8.5 runs?",
    scope: "over",
    outcomes: [
      { key: "over",  label: "Over 8.5 Runs",  emoji: "⬆️", color: "#2ecc71" },
      { key: "under", label: "Under 8.5 Runs", emoji: "⬇️", color: "#3498db" }
    ]
  },
  batsman_boundary: {
    label: "Batsman Hits Boundary?",
    icon: "💫",
    description: "Will the striker hit at least one 4 or 6 this over?",
    scope: "over",
    outcomes: [
      { key: "yes", label: "YES — Boundary", emoji: "✅", color: "#2ecc71" },
      { key: "no",  label: "NO — No Boundary", emoji: "❌", color: "#e74c3c" }
    ]
  },
  wicket: {
    label: "Wicket in Over?",
    icon: "🎳",
    description: "Will any wicket fall in this over?",
    scope: "over",
    outcomes: [
      { key: "yes", label: "YES — Wicket Falls", emoji: "💀", color: "#e74c3c" },
      { key: "no",  label: "NO — Safe Over",     emoji: "🛡️", color: "#2ecc71" }
    ]
  },
  match_winner: {
    label: "Match Winner",
    icon: "🏆",
    description: "Who will win the match? Odds shift ball by ball.",
    scope: "match",
    outcomes: [
      { key: "teamA", label: "Batting Team",  emoji: "🏏", color: "#f39c12" },
      { key: "teamB", label: "Bowling Team",  emoji: "⚡", color: "#3498db" }
    ]
  }
};

// Initial pool stakes seeded to give house advantage (bots will skew further)
const INITIAL_POOLS = {
  next_ball:        { dot: 600, single: 400, four: 250, six: 100, wicket: 80, extra: 50 },
  dismissal:        { caught: 200, bowled: 150, lbw: 100, run_out: 80, stumped: 70 },
  over_runs:        { over: 1200, under: 800 },
  batsman_boundary: { yes: 300, no: 900 },
  wicket:           { yes: 400, no: 1600 },
  match_winner:     { teamA: 5000, teamB: 5000 }
};

export default function LiveInPlayDashboard({
  match,
  username,
  points,
  setPoints,
  setLockedPoints,
  onClose,
  fetchMyBets,
  fetchAllHistory,
  fetchLeaderboard,
  toast
}) {
  // ── Simulation state ──────────────────────────────────────────────────────
  const [overs, setOvers] = useState(12);
  const [balls, setBalls] = useState(0);
  const [runs, setRuns] = useState(92);
  const [wickets, setWickets] = useState(3);
  const [batsmen, setBatsmen] = useState([
    { name: "V. Kohli", runs: 42, balls: 31, isStriker: true },
    { name: "G. Maxwell", runs: 15, balls: 8, isStriker: false }
  ]);
  const [bowler, setBowler] = useState({ name: "J. Bumrah", overs: 2.0, runs: 14, wickets: 1 });
  const [recentDeliveries, setRecentDeliveries] = useState([]);
  const [currentOverRuns, setCurrentOverRuns] = useState(0);
  const [currentOverWickets, setCurrentOverWickets] = useState(0);
  const [batsmanHadBoundaryThisOver, setBatsmanHadBoundaryThisOver] = useState(false);
  const [isDeliveryInProgress, setIsDeliveryInProgress] = useState(false);
  const [deliveryResult, setDeliveryResult] = useState("");
  const [deliveryFlash, setDeliveryFlash] = useState(null); // { text, color }
  const [commentary, setCommentary] = useState([
    "Bumrah to Kohli, no run, driven firmly to mid-off.",
    "Bumrah to Kohli, 1 run, tucked away to deep square leg.",
    "Welcome to live in-play wagering! Next over is starting soon."
  ]);

  // ── Betting state ─────────────────────────────────────────────────────────
  const [isSuspended, setIsSuspended] = useState(false);
  const [activeMarketTab, setActiveMarketTab] = useState("ball"); // ball | over | match
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [betAmount, setBetAmount] = useState("");
  const [countdown, setCountdown] = useState(null);
  const [isPlacingBet, setIsPlacingBet] = useState(false);
  const [slotTiers, setSlotTiers] = useState([15, 20, 100, 500]);
  const [activeLiveBets, setActiveLiveBets] = useState([]);
  const [betHistory, setBetHistory] = useState([]);

  // ── Pool stakes (Pari-Mutuel) ─────────────────────────────────────────────
  const [poolStakes, setPoolStakes] = useState(INITIAL_POOLS);
  const [oddsFlash, setOddsFlash] = useState({}); // { "market.outcome": "up"|"down" }
  const [botBetsFeed, setBotBetsFeed] = useState([
    "🤖 BoundaryBot placed 500 pebbles on UNDER 8.5 Runs",
    "🤖 MahiFanatic placed 1000 pebbles on Wicket NO"
  ]);

  // ── Room state ────────────────────────────────────────────────────────────
  const [currentRoom, setCurrentRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomType, setNewRoomType] = useState("public");
  const [newRoomPassword, setNewRoomPassword] = useState("");
  const [newRoomEntryFee, setNewRoomEntryFee] = useState(15);
  const [passwordPromptRoom, setPasswordPromptRoom] = useState(null);
  const [roomPasswordInput, setRoomPasswordInput] = useState("");

  const simInterval = useRef(null);
  const countdownInterval = useRef(null);
  const oddsFlashTimeout = useRef({});

  // ── Sound ─────────────────────────────────────────────────────────────────
  const playSound = (type) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      if (type === "click") {
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(600, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.05);
      } else if (type === "win") {
        oscillator.type = "triangle";
        oscillator.frequency.setValueAtTime(523.25, audioCtx.currentTime);
        oscillator.frequency.setValueAtTime(659.25, audioCtx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(783.99, audioCtx.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.15, audioCtx.currentTime);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.35);
      } else if (type === "lock") {
        oscillator.type = "square";
        oscillator.frequency.setValueAtTime(200, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.08, audioCtx.currentTime);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.15);
      }
    } catch (e) {}
  };

  // ── Live odds calculation ──────────────────────────────────────────────────
  const computeOdds = (marketKey) => {
    const stakes = poolStakes[marketKey];
    if (!stakes) return {};
    const total = Object.values(stakes).reduce((a, b) => a + b, 0);
    const netPool = total * 0.90; // 10% house rake
    const oddsMap = {};
    Object.entries(stakes).forEach(([outcome, stake]) => {
      const raw = stake > 0 ? netPool / stake : 1.85;
      oddsMap[outcome] = parseFloat(Math.min(50, Math.max(1.05, raw)).toFixed(2));
    });
    return oddsMap;
  };

  // Flash odds when they change (up=green, down=red)
  const flashOdds = (marketKey, outcome, direction) => {
    const flashKey = `${marketKey}.${outcome}`;
    setOddsFlash(prev => ({ ...prev, [flashKey]: direction }));
    if (oddsFlashTimeout.current[flashKey]) clearTimeout(oddsFlashTimeout.current[flashKey]);
    oddsFlashTimeout.current[flashKey] = setTimeout(() => {
      setOddsFlash(prev => { const next = { ...prev }; delete next[flashKey]; return next; });
    }, 800);
  };

  // ── Pool update & odds flash ───────────────────────────────────────────────
  const updatePool = (marketKey, outcome, amount) => {
    setPoolStakes(prev => {
      const prevOdds = computeOdds(marketKey);
      const next = { ...prev, [marketKey]: { ...prev[marketKey], [outcome]: (prev[marketKey][outcome] || 0) + amount } };
      // Calculate new odds after update
      const newStakes = next[marketKey];
      const newTotal = Object.values(newStakes).reduce((a, b) => a + b, 0);
      const newNet = newTotal * 0.90;
      const newOdd = newStakes[outcome] > 0 ? newNet / newStakes[outcome] : 1.85;
      const prevOdd = prevOdds[outcome] || 1.85;
      if (Math.abs(newOdd - prevOdd) > 0.05) {
        flashOdds(marketKey, outcome, newOdd > prevOdd ? "up" : "down");
      }
      return next;
    });
  };

  // ── Rooms ─────────────────────────────────────────────────────────────────
  const fetchRooms = async () => {
    setLoadingRooms(true);
    try {
      const res = await fetch(`${API}/matches/${match.matchId}/rooms`);
      if (res.ok) setRooms(await res.json());
    } catch (err) { console.error("Failed to load rooms:", err); }
    setLoadingRooms(false);
  };

  useEffect(() => { fetchRooms(); }, [match.matchId]);

  useEffect(() => {
    fetch(`${API}/slots/settings`)
      .then(res => res.json())
      .then(data => { if (data.activeTiers) setSlotTiers(data.activeTiers); })
      .catch(() => {});
  }, []);

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!newRoomName.trim()) { toast.error("Room Name Required", "Enter a valid room name."); return; }
    try {
      const res = await fetch(`${API}/matches/${match.matchId}/rooms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roomName: newRoomName.trim(), roomType: newRoomType, password: newRoomPassword, entryFee: Number(newRoomEntryFee), creator: username })
      });
      const data = await res.json();
      if (res.ok) {
        const createdRoom = data.room || data;
        toast.success("Room Created", `Successfully created room: ${createdRoom.roomName}`);
        if (data.updatedPoints !== undefined) setPoints(data.updatedPoints);
        setIsCreatingRoom(false);
        setNewRoomName("");
        setNewRoomPassword("");
        handleJoinRoom(createdRoom);
      } else {
        toast.error("Creation Failed", data.message || "Failed to create room.");
      }
    } catch (err) { toast.error("Error", "Failed to connect to backend."); }
  };

  const handleJoinRoom = async (room, password = "") => {
    try {
      const res = await fetch(`${API}/rooms/${room._id}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentRoom(data.room);
        setBetAmount(String(data.room.entryFee));
        setPasswordPromptRoom(null);
        setRoomPasswordInput("");
        toast.success("Joined Room", `Welcome to ${data.room.roomName}!`);
        fetchRooms();
      } else {
        toast.error("Join Failed", data.message || "Could not join room.");
      }
    } catch (err) { toast.error("Error", "Failed to connect to backend."); }
  };

  const handleLeaveRoom = async () => {
    if (!currentRoom) return;
    try {
      await fetch(`${API}/rooms/${currentRoom._id}/leave`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username })
      });
    } catch (err) {}
    setCurrentRoom(null);
    setBetAmount("");
    toast.info("Left Room", "You have returned to the lobby.");
    fetchRooms();
  };

  // ── Match initialization ───────────────────────────────────────────────────
  useEffect(() => {
    const hash = match.matchId ? match.matchId.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) : 100;
    const baseOvers = 10 + (hash % 8);
    setOvers(baseOvers);
    setWickets(hash % 5);
    setRuns(80 + (hash % 60));
    setBalls(0);
    setRecentDeliveries([]);
    setCurrentOverRuns(0);
    setCurrentOverWickets(0);
    setBatsmanHadBoundaryThisOver(false);
    const names = [
      ["V. Kohli", "G. Maxwell", "J. Bumrah"],
      ["R. Sharma", "H. Pandya", "M. Santner"],
      ["MS Dhoni", "R. Jadeja", "M. Pathirana"],
      ["S. Samson", "Y. Chahal", "T. Boult"],
      ["S. Iyer", "A. Russell", "M. Starc"],
      ["R. Pant", "A. Patel", "K. Yadav"],
      ["K. L. Rahul", "N. Pooran", "R. Bishnoi"],
      ["P. Cummins", "T. Head", "B. Kumar"],
      ["S. Dhawan", "L. Livingstone", "K. Rabada"],
      ["S. Gill", "R. Khan", "U. Yadav"]
    ];
    const n = names[hash % names.length];
    setBatsmen([
      { name: n[0], runs: 30 + (hash % 25), balls: 20 + (hash % 15), isStriker: true },
      { name: n[1], runs: 12 + (hash % 15), balls: 8 + (hash % 8), isStriker: false }
    ]);
    setBowler({ name: n[2], overs: 1.0 + (hash % 2) / 2, runs: 10 + (hash % 12), wickets: hash % 2 });
    setPoolStakes(JSON.parse(JSON.stringify(INITIAL_POOLS)));
    return () => {
      if (simInterval.current) clearInterval(simInterval.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [match]);

  // ── Seed bots ─────────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/live-bet/seed-bots`, { method: "POST" }).catch(() => {});
  }, []);

  // ── Bot bet trigger (multi-market) ────────────────────────────────────────
  const triggerBotBets = async (marketTypes, ballNum = 0) => {
    try {
      const markets = Array.isArray(marketTypes) ? marketTypes : [marketTypes];
      const res = await fetch(`${API}/live-bet/place-bot-bets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ matchId: match.matchId, overNum: overs + 1, marketType: markets, targetBall: ballNum })
      });
      const data = await res.json();
      if (res.ok && data.bets && data.bets.length > 0) {
        data.bets.forEach(bet => {
          const mkt = bet.market || "over_runs";
          const label = bet.teamLabel || bet.choice;
          setBotBetsFeed(prev => [
            `🤖 ${bet.username.replace("🤖 ", "")} bet ${bet.amount} PB on ${label.toUpperCase()} [${mkt.replace(/_/g, " ")}]`,
            ...prev.slice(0, 12)
          ]);
          if (bet.choice && INITIAL_POOLS[mkt]) {
            updatePool(mkt, bet.choice, bet.amount);
          }
        });
      }
    } catch (err) { console.warn("Bot bets failed:", err); }
  };

  // ── Simulation loop ───────────────────────────────────────────────────────
  useEffect(() => {
    simInterval.current = setInterval(() => {
      setIsSuspended(true);
      setIsDeliveryInProgress(true);
      setDeliveryResult("🎳 Bowler runs in...");
      playSound("click");
      setTimeout(() => { simulateBallDelivery(); }, 3000);
    }, 12000);
    return () => { if (simInterval.current) clearInterval(simInterval.current); };
  }, [balls, overs, runs, wickets, batsmen, bowler, recentDeliveries, currentOverRuns, currentOverWickets, batsmanHadBoundaryThisOver]);

  const simulateBallDelivery = () => {
    const roll = Math.random();
    let ballRuns = 0;
    let isWicket = false;
    let isExtra = false;
    let dismissalType = "";
    let desc = "";

    // Probability: dot 32%, 1-run 35%, 2-run 10%, 4 13%, 6 5%, Wicket 3%, Wide 2%
    if (roll < 0.32)      { ballRuns = 0; desc = "no run, beaten in flight, beats the bat."; }
    else if (roll < 0.67) { ballRuns = 1; desc = "1 run, flicked gently off the pads to square leg."; }
    else if (roll < 0.77) { ballRuns = 2; desc = "2 runs, driven through extra cover, good running!"; }
    else if (roll < 0.90) { ballRuns = 4; desc = "FOUR! Cracking shot over the bowler's head!"; }
    else if (roll < 0.95) { ballRuns = 6; desc = "SIX! Monstrous! Pulled high into the deep stands!"; }
    else if (roll < 0.98) {
      isWicket = true;
      // Dismissal probabilities: caught 35%, bowled 28%, lbw 18%, run_out 12%, stumped 7%
      const dr = Math.random();
      if (dr < 0.35)      { dismissalType = "caught";  desc = "OUT! CAUGHT! Caught brilliantly at slip!"; }
      else if (dr < 0.63) { dismissalType = "bowled";  desc = "OUT! BOWLED! Clean bowled, off stump flying!"; }
      else if (dr < 0.81) { dismissalType = "lbw";     desc = "OUT! LBW! Plumb in front, finger goes up!"; }
      else if (dr < 0.93) { dismissalType = "run_out"; desc = "OUT! RUN OUT! Direct hit, caught short of crease!"; }
      else                 { dismissalType = "stumped"; desc = "OUT! STUMPED! Beaten in flight, keeper takes the bails!"; }
    } else {
      isExtra = true;
      ballRuns = 1;
      desc = "Wide! Down the leg side, umpire extends both arms.";
    }

    const ballOutcome = isWicket ? "wicket" : isExtra ? "extra" : ballRuns === 0 ? "dot" : ballRuns === 1 ? "single" : ballRuns === 4 ? "four" : ballRuns === 6 ? "six" : "single";

    const striker = batsmen.find(b => b.isStriker);
    const nextBalls = isExtra ? balls : balls + 1; // wides don't count as a ball bowled
    const nextOverRuns = currentOverRuns + ballRuns;
    const nextOverWickets = currentOverWickets + (isWicket ? 1 : 0);
    const hasBoundary = batsmanHadBoundaryThisOver || ballRuns === 4 || ballRuns === 6;
    const newRecent = isExtra ? recentDeliveries : [...recentDeliveries, isWicket ? "W" : String(ballRuns)];

    const ballCommentary = `${bowler.name} to ${striker.name}, ${desc}`;

    // Flash delivery result
    const flashColor = isWicket ? "#e74c3c" : ballRuns === 6 ? "#f39c12" : ballRuns === 4 ? "#2ecc71" : "#fff";
    const flashText = isWicket ? `💥 ${dismissalType.toUpperCase()}!` : ballRuns === 6 ? "🔥 MAGNIFICENT SIX!" : ballRuns === 4 ? "🏏 FOUR!" : isExtra ? "↩️ WIDE!" : `${ballRuns} Run(s)`;
    setDeliveryFlash({ text: flashText, color: flashColor });
    setTimeout(() => setDeliveryFlash(null), 3000);

    setRuns(prev => prev + ballRuns);
    setWickets(prev => prev + (isWicket ? 1 : 0));
    setBalls(nextBalls);
    setCurrentOverRuns(nextOverRuns);
    setCurrentOverWickets(nextOverWickets);
    setBatsmanHadBoundaryThisOver(hasBoundary);
    setRecentDeliveries(newRecent);
    setCommentary(prev => [ballCommentary, ...prev.slice(0, 8)]);
    setDeliveryResult(flashText);

    setBatsmen(prev => prev.map(b => {
      if (b.isStriker) {
        if (isWicket) {
          const newNames = ["D. Karthik", "S. Iyer", "R. Jadeja", "H. Pandya", "S. Gill", "R. Sharma"];
          return { name: newNames[Math.floor(Math.random() * newNames.length)], runs: 0, balls: 0, isStriker: true };
        }
        return { ...b, runs: b.runs + ballRuns, balls: b.balls + 1, isStriker: ballRuns % 2 === 0 };
      }
      return { ...b, isStriker: ballRuns % 2 !== 0 };
    }));
    setBowler(prev => ({ ...prev, runs: prev.runs + ballRuns, wickets: prev.wickets + (isWicket ? 1 : 0) }));

    // Update ball pool stakes based on actual outcome
    updatePool("next_ball", ballOutcome, Math.floor(Math.random() * 200 + 50));
    if (isWicket) updatePool("dismissal", dismissalType, Math.floor(Math.random() * 200 + 50));

    // Settle ball-level markets then continue
    if (nextBalls === 6) {
      setTimeout(() => {
        settleAllOverBets(nextOverRuns, nextOverWickets, hasBoundary, ballOutcome, dismissalType);
      }, 2000);
    } else {
      // Settle ball-level (next_ball) market immediately
      if (currentRoom) {
        settleBallMarket("next_ball", ballOutcome, nextBalls);
        if (isWicket) settleBallMarket("dismissal", dismissalType, nextBalls);
      }
      // Recalculate over market odds based on new score
      recalculateOverOdds(nextOverRuns, nextBalls);
      triggerBotBets(["next_ball", "over_runs", "batsman_boundary", "wicket"], nextBalls + 1);
      setIsSuspended(false);
      setIsDeliveryInProgress(false);
    }
  };

  const recalculateOverOdds = (overRuns, ballsBowled) => {
    const target = 8.5;
    const ballsLeft = 6 - ballsBowled;
    if (ballsLeft <= 0) return;
    const projected = overRuns + (ballsLeft * 1.35);
    const diff = projected - target;
    if (diff > 0) {
      updatePool("over_runs", "over", 150 + Math.floor(Math.random() * 200));
      updatePool("over_runs", "under", 20 + Math.floor(Math.random() * 40));
    } else {
      updatePool("over_runs", "under", 150 + Math.floor(Math.random() * 200));
      updatePool("over_runs", "over", 20 + Math.floor(Math.random() * 40));
    }
  };

  const settleBallMarket = async (market, winningOutcome, ballNum) => {
    if (!currentRoom) return;
    try {
      await fetch(`${API}/rooms/${currentRoom._id}/settle-ball`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          matchId: match.matchId,
          targetOver: overs + 1,
          targetBall: ballNum,
          market,
          winningOutcome
        })
      });
    } catch (err) { console.warn("Ball settle failed:", err); }
  };

  const settleAllOverBets = async (finalRuns, finalWickets, hadBoundary, lastBallOutcome, lastDismissal) => {
    setIsSuspended(true);
    setIsDeliveryInProgress(true);
    setDeliveryResult(`⌛ Settling Over ${overs + 1}...`);

    const winningOverRuns = finalRuns > 8.5 ? "over" : "under";
    const winningWicket = finalWickets > 0 ? "yes" : "no";
    const winningBoundary = hadBoundary ? "yes" : "no";

    try {
      // Settle all 4 over-wide markets + 2 ball-level markets simultaneously
      const [resRuns, resWicket, resBoundary] = await Promise.all([
        fetch(`${API}/live-bet/settle-pool`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ matchId: match.matchId, overNum: overs + 1, marketType: "over_runs", winningOutcome: winningOverRuns })
        }),
        fetch(`${API}/live-bet/settle-pool`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ matchId: match.matchId, overNum: overs + 1, marketType: "wicket", winningOutcome: winningWicket })
        }),
        // Settle batsman_boundary and match_winner via settle-ball route if in a room
        currentRoom ? fetch(`${API}/rooms/${currentRoom._id}/settle-ball`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ matchId: match.matchId, targetOver: overs + 1, targetBall: 0, market: "batsman_boundary", winningOutcome: winningBoundary })
        }) : Promise.resolve({ ok: false })
      ]);

      const dataRuns = resRuns.ok ? await resRuns.json() : {};
      const dataWicket = resWicket.ok ? await resWicket.json() : {};
      const dataBoundary = resBoundary.ok ? await resBoundary.json() : {};

      // Also settle ball market for last ball
      if (currentRoom) {
        await settleBallMarket("next_ball", lastBallOutcome, 6);
        if (lastDismissal) await settleBallMarket("dismissal", lastDismissal, 6);
      }

      const allSettled = [
        ...(dataRuns.settled || []),
        ...(dataWicket.settled || []),
        ...(dataBoundary.settled || [])
      ];

      const mySettled = allSettled.filter(s => s.username === username);
      if (mySettled.length > 0) {
        let totalWon = 0, totalLost = 0;
        mySettled.forEach(bet => {
          if (bet.outcome === "won") totalWon += bet.payout;
          else if (bet.outcome === "lost") totalLost += bet.amount;
        });
        if (totalWon > 0) {
          toast.success("WINNER! 🎉", `+${totalWon} pebbles won on Over ${overs + 1}!`, 4000);
          playSound("win");
          confetti({ particleCount: 100, spread: 80, origin: { y: 0.8 } });
        } else if (totalLost > 0) {
          toast.error("Lost 😢", `-${totalLost} pebbles on Over ${overs + 1}`, 3000);
        }

        let payoutCredit = 0, returnedLocked = 0;
        mySettled.forEach(bet => {
          returnedLocked += bet.amount;
          if (bet.outcome === "won") payoutCredit += bet.payout;
        });
        setPoints(prev => prev + payoutCredit);
        setLockedPoints(prev => Math.max(0, prev - returnedLocked));
      }

      const totalPool = (dataRuns.totalPool || 0) + (dataWicket.totalPool || 0) + (dataBoundary.totalPool || 0);
      const totalHouse = (dataRuns.houseCut || 0) + (dataWicket.houseCut || 0) + (dataBoundary.houseCut || 0);
      if (totalPool > 0) {
        toast.info("Over Settled", `Over ${overs + 1}: ${finalRuns} Runs, ${finalWickets} Wkt(s). Pool: ${totalPool} PB. House Rake: ${totalHouse} PB 🏦`, 5000);
      }

      // Update settled bets history
      const newHistory = activeLiveBets.map(bet => {
        const winMap = { over_runs: winningOverRuns, wicket: winningWicket, batsman_boundary: winningBoundary };
        const winChoice = winMap[bet.marketType] || "";
        const isWon = bet.choice === winChoice;
        const matching = mySettled.find(s => s.amount === bet.amount && s.outcome === (isWon ? "won" : "lost"));
        return { ...bet, status: isWon ? "won" : "lost", winAmount: matching ? matching.payout : 0 };
      });
      setBetHistory(prev => [...newHistory, ...prev]);

      fetchMyBets();
      fetchAllHistory();
      fetchLeaderboard();
    } catch (err) { console.error("Settlement failed:", err); }

    // Reset for next over
    setActiveLiveBets([]);
    setPoolStakes(JSON.parse(JSON.stringify(INITIAL_POOLS)));
    setBotBetsFeed([]);
    setOvers(prev => prev + 1);
    setBalls(0);
    setRecentDeliveries([]);
    setCurrentOverRuns(0);
    setCurrentOverWickets(0);
    setBatsmanHadBoundaryThisOver(false);

    const bowlNames = ["R. Ashwin", "K. Pandya", "S. Thakur", "M. Siraj", "A. Nortje", "J. Hazlewood"];
    setBowler({ name: bowlNames[Math.floor(Math.random() * bowlNames.length)], overs: 0, runs: 0, wickets: 0 });

    setIsSuspended(false);
    setIsDeliveryInProgress(false);
  };

  // ── Cash out handler ───────────────────────────────────────────────────────
  const handleCashOut = async (bet) => {
    const currentOdds = computeOdds(bet.marketType)[bet.choice] || 1.85;
    try {
      const res = await fetch(`${API}/live-bet/cashout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ betId: bet._id, username, currentLiveOdds: currentOdds })
      });
      const data = await res.json();
      if (res.ok) {
        setPoints(data.points);
        setLockedPoints(data.lockedPoints);
        setActiveLiveBets(prev => prev.filter(b => b._id !== bet._id));
        setBetHistory(prev => [{ ...bet, status: "cashed_out", winAmount: data.cashOutValue }, ...prev]);
        toast.success("Cashed Out 💰", `Received ${data.cashOutValue} PB (House fee: ${bet.amount - data.cashOutValue} PB)`, 3000);
      } else {
        toast.error("Cash Out Failed", data.message || "Could not cash out.");
      }
    } catch (err) { toast.error("Error", "Network issue during cash out."); }
  };

  // ── Bet placement ─────────────────────────────────────────────────────────
  const selectOutcome = (market, outcome) => {
    playSound("click");
    setSelectedMarket(market);
    setSelectedOutcome(outcome);
    if (!currentRoom) setBetAmount("");
  };

  const startBetCountdown = () => {
    if (isSuspended) { toast.error("Market Suspended", "Wait for the ball delivery to complete."); return; }
    if (!selectedMarket || !selectedOutcome) { toast.error("No Selection", "Pick a market outcome first."); return; }
    const amt = currentRoom ? currentRoom.entryFee : parseInt(betAmount);
    if (!amt || amt <= 0 || amt > points) { toast.error("Invalid Amount", "Enter a valid predictor stake!"); return; }
    playSound("lock");
    setCountdown(5);
    countdownInterval.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(countdownInterval.current); placeLiveBet(); return null; }
        return prev - 1;
      });
    }, 1000);
  };

  const cancelCountdown = () => {
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    setCountdown(null);
    toast.info("Bet Cancelled", "Lock cancelled before countdown finished.", 2000);
  };

  const placeLiveBet = async () => {
    setIsPlacingBet(true);
    const amount = currentRoom ? currentRoom.entryFee : parseInt(betAmount);
    const liveOdds = computeOdds(selectedMarket)[selectedOutcome] || 1.85;

    try {
      let res, data;
      if (currentRoom) {
        // Room bet (LiveRoomBet) — all new markets
        res = await fetch(`${API}/rooms/${currentRoom._id}/place-bet`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username,
            market: selectedMarket,
            outcome: selectedOutcome,
            targetOver: overs + 1,
            targetBall: MARKETS[selectedMarket]?.scope === "ball" ? balls + 1 : 0,
            initialOdds: liveOdds
          })
        });
        data = await res.json();
      } else {
        // Standard live bet (Bet collection) for over_runs and wicket only
        const isSupportedLegacy = ["over_runs", "wicket"].includes(selectedMarket);
        if (!isSupportedLegacy) {
          toast.error("Join a Room", "Ball-by-ball and Match Winner markets require joining a gaming room first.");
          setIsPlacingBet(false);
          return;
        }
        const teamLabel = selectedMarket === "over_runs"
          ? (selectedOutcome === "over" ? "Over 8.5" : "Under 8.5")
          : (selectedOutcome === "yes" ? "Wicket: Yes" : "Wicket: No");
        res = await fetch(`${API}/live-bet`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: username, amount, matchId: match.matchId,
            matchLabel: `[LIVE] Over ${overs + 1} ${selectedMarket === "over_runs" ? "Runs" : "Wicket"}`,
            team: teamLabel, odds: liveOdds
          })
        });
        data = await res.json();
      }

      if (res.ok) {
        setPoints(data.points);
        setLockedPoints(data.lockedPoints);
        setActiveLiveBets(prev => [...prev, {
          _id: data.bet?._id || Date.now().toString(),
          marketType: selectedMarket,
          choice: selectedOutcome,
          amount,
          odds: liveOdds,
          over: overs + 1,
          status: "pending"
        }]);
        toast.bet(`🔒 ${amount} PB locked on ${(MARKETS[selectedMarket]?.outcomes.find(o => o.key === selectedOutcome)?.label || selectedOutcome).toUpperCase()} @ ${liveOdds}x`, 3000);
        setSelectedMarket(null);
        setSelectedOutcome(null);
        if (!currentRoom) setBetAmount("");
        fetchMyBets();
        fetchAllHistory();
      } else {
        toast.error("Placement Failed", data.message || "Something went wrong");
      }
    } catch (err) { toast.error("Network Error", "Can't connect to server!"); }
    setIsPlacingBet(false);
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const getOddsDisplay = (marketKey, outcomeKey) => {
    const odds = computeOdds(marketKey);
    return odds[outcomeKey] || 1.85;
  };

  const getBallTabMarkets = () => ["next_ball", "dismissal"];
  const getOverTabMarkets = () => ["over_runs", "batsman_boundary", "wicket"];

  const TAB_BALL_MARKETS = getBallTabMarkets();
  const TAB_OVER_MARKETS = getOverTabMarkets();

  const activeTabMarkets = activeMarketTab === "ball" ? TAB_BALL_MARKETS : activeMarketTab === "over" ? TAB_OVER_MARKETS : ["match_winner"];

  const getBallColor = (ball) => {
    if (ball === "6" || ball === "4") return { bg: "rgba(46,204,113,0.2)", border: "#2ecc71", color: "#2ecc71" };
    if (ball === "W") return { bg: "rgba(231,76,60,0.2)", border: "#e74c3c", color: "#e74c3c" };
    return { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.15)", color: "#fff" };
  };

  const totalPool = (mkt) => Object.values(poolStakes[mkt] || {}).reduce((a, b) => a + b, 0);

  return (
    <div style={{ padding: "16px 20px", background: "rgba(13,13,18,0.98)", borderRadius: 16, border: "1px solid rgba(255,255,255,0.08)", maxWidth: 760, margin: "0 auto", color: "#fff", boxShadow: "0 16px 48px rgba(0,0,0,0.7)" }}>
      {/* ── HEADER ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ background: "#EF0107", color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 6, letterSpacing: "0.06em", animation: "blink 1.2s infinite" }}>🔴 LIVE IN-PLAY</span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{match.matchLabel}</span>
        </div>
        <button onClick={onClose} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, color: "rgba(255,255,255,0.7)", padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>✕ Close</button>
      </div>

      {/* ── ROOM LOBBY or ACTIVE ROOM ── */}
      {!currentRoom ? (
        <div style={{ marginTop: 8 }}>
          {passwordPromptRoom && (
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.8)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ background: "#1a1a2e", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 16, padding: 24, width: 340 }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>🔒 Private Room</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginBottom: 16 }}>{passwordPromptRoom.roomName}</div>
                <input type="password" value={roomPasswordInput} onChange={e => setRoomPasswordInput(e.target.value)} placeholder="Enter room password..." style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 14, marginBottom: 12, boxSizing: "border-box" }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setPasswordPromptRoom(null); setRoomPasswordInput(""); }} style={{ flex: 1, padding: 10, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "transparent", color: "rgba(255,255,255,0.6)", cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => handleJoinRoom(passwordPromptRoom, roomPasswordInput)} style={{ flex: 1, padding: 10, borderRadius: 8, border: "none", background: "var(--accent)", color: "#000", fontWeight: 700, cursor: "pointer" }}>Enter Room</button>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>🏠 Room Lobby</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>Join a room to unlock all 6 live markets + ball-by-ball gaming.</div>
            </div>
            <button onClick={() => setIsCreatingRoom(!isCreatingRoom)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--accent)", background: isCreatingRoom ? "var(--accent)" : "transparent", color: isCreatingRoom ? "#000" : "var(--accent)", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
              {isCreatingRoom ? "✕ Cancel" : "+ Create Room"}
            </button>
          </div>
          {isCreatingRoom && (
            <form onSubmit={handleCreateRoom} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>🛠️ Create New Room</div>
              <div style={{ background: "rgba(255,193,7,0.05)", border: "1px solid rgba(255,193,7,0.15)", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#ffc107", marginBottom: 12 }}>
                ⚠️ Custom rooms require wagering over 10,000 pebbles. House creation fee: <strong>{Math.ceil(newRoomEntryFee * 0.05)} pebbles (5%)</strong>.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", display: "block", marginBottom: 4 }}>Room Name</label>
                  <input value={newRoomName} onChange={e => setNewRoomName(e.target.value)} placeholder="e.g. My Squad" style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 13, boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", display: "block", marginBottom: 4 }}>Entry Fee (Pebbles)</label>
                  <input type="number" min="1" value={newRoomEntryFee} onChange={e => setNewRoomEntryFee(Math.max(1, parseInt(e.target.value) || 0))} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 13, boxSizing: "border-box" }} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", display: "block", marginBottom: 4 }}>Room Type</label>
                  <select value={newRoomType} onChange={e => setNewRoomType(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "#1a1a2e", color: "#fff", fontSize: 13, boxSizing: "border-box" }}>
                    <option value="public">🌐 Public</option>
                    <option value="private">🔒 Private</option>
                  </select>
                </div>
                {newRoomType === "private" && (
                  <div>
                    <label style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", display: "block", marginBottom: 4 }}>Password</label>
                    <input type="password" value={newRoomPassword} onChange={e => setNewRoomPassword(e.target.value)} placeholder="Set password..." style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 13, boxSizing: "border-box" }} />
                  </div>
                )}
              </div>
              <button type="submit" style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", background: "var(--accent)", color: "#000", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Create & Join Room →</button>
            </form>
          )}
          {loadingRooms ? (
            <div style={{ textAlign: "center", padding: 30, color: "rgba(255,255,255,0.4)" }}>Loading rooms...</div>
          ) : rooms.length === 0 ? (
            <div style={{ textAlign: "center", padding: 30, color: "rgba(255,255,255,0.4)" }}>No rooms available. Create one to start playing!</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {rooms.map(room => (
                <div key={room._id} onClick={() => room.roomType === "private" ? setPasswordPromptRoom(room) : handleJoinRoom(room)} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", transition: "all 0.2s" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: room.roomType === "private" ? "rgba(239,1,7,0.15)" : "rgba(0,200,150,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{room.roomType === "private" ? "🔒" : "🌐"}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{room.roomName}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>{room.roomType === "private" ? "Private" : "Public"} • By {room.creator}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, fontSize: 16, color: "var(--accent)" }}>{room.entryFee} pts</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>👥 {room.participants?.length || 0}/{room.maxParticipants}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* ── ROOM BANNER ── */}
          <div style={{ background: "linear-gradient(135deg, rgba(0,200,150,0.08), rgba(0,200,150,0.02))", border: "1px solid rgba(0,200,150,0.2)", borderRadius: 12, padding: "10px 16px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 20 }}>{currentRoom.roomType === "private" ? "🔒" : "🌐"}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{currentRoom.roomName}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>Fixed Stake: <strong style={{ color: "var(--accent)" }}>{currentRoom.entryFee} PB</strong> • 👥 {currentRoom.participants?.length || 0} players • <span style={{ color: "#ffc107" }}>10% House Rake Active</span></div>
              </div>
            </div>
            <button onClick={handleLeaveRoom} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(239,1,7,0.4)", background: "rgba(239,1,7,0.1)", color: "#EF0107", fontWeight: 600, cursor: "pointer", fontSize: 11 }}>Leave ←</button>
          </div>

          {/* ── SCOREBOARD ── */}
          <div style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 36, fontWeight: 900, color: "#fff", lineHeight: 1, fontFamily: "monospace" }}>{runs}/{wickets}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>Overs: {overs}.{balls} (RR: {(runs / ((overs * 6 + balls) / 6 || 1)).toFixed(2)})</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: "0.05em" }}>CURRENT BOWLER</div>
                <div style={{ fontWeight: 700, color: "var(--accent)", fontSize: 14 }}>{bowler.name}</div>
                <div style={{ fontSize: 12 }}>{bowler.wickets}-{bowler.runs} ({bowler.overs}ov)</div>
              </div>
            </div>

            {/* Batsmen */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              {batsmen.map((b, i) => (
                <div key={i} style={{ background: b.isStriker ? "rgba(29,158,117,0.08)" : "rgba(255,255,255,0.02)", borderLeft: b.isStriker ? "3px solid var(--accent)" : "3px solid transparent", padding: "7px 12px", borderRadius: 8, display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{b.name} {b.isStriker ? "🏏" : ""}</span>
                  <span style={{ fontFamily: "monospace", color: "var(--accent)", fontWeight: 700 }}>{b.runs}({b.balls})</span>
                </div>
              ))}
            </div>

            {/* Over balls */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>Over {overs + 1}:</div>
              <div style={{ display: "flex", gap: 5 }}>
                {recentDeliveries.map((ball, i) => {
                  const c = getBallColor(ball);
                  return <span key={i} style={{ width: 24, height: 24, borderRadius: "50%", background: c.bg, border: `1px solid ${c.border}`, color: c.color, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace" }}>{ball}</span>;
                })}
                {Array.from({ length: 6 - balls }).map((_, i) => (
                  <span key={i} style={{ width: 24, height: 24, borderRadius: "50%", border: "1px dashed rgba(255,255,255,0.2)", background: "transparent" }} />
                ))}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)" }}>{currentOverRuns} Runs</div>
            </div>

            {/* Delivery status & Commentary */}
            <div style={{ background: "rgba(0,0,0,0.25)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 14px", textAlign: "center" }}>
              {deliveryFlash ? (
                <div style={{ fontSize: 20, fontWeight: 900, color: deliveryFlash.color, animation: "pulse 0.5s ease", transition: "all 0.3s", marginBottom: 6 }}>{deliveryFlash.text}</div>
              ) : (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, background: isDeliveryInProgress ? "rgba(239,1,7,0.08)" : "rgba(255,255,255,0.04)", border: `1px solid ${isDeliveryInProgress ? "#EF0107" : "rgba(255,255,255,0.1)"}`, padding: "4px 14px", borderRadius: 99, color: isDeliveryInProgress ? "#EF0107" : "#fff", marginBottom: 6 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: isDeliveryInProgress ? "#EF0107" : "rgba(255,255,255,0.3)", animation: isDeliveryInProgress ? "blink 1s infinite" : "none", display: "inline-block" }} />
                  <span style={{ fontWeight: 700 }}>{deliveryResult || "Waiting for bowler..."}</span>
                </div>
              )}
              <div style={{ fontSize: 11, fontStyle: "italic", color: "rgba(255,255,255,0.55)", lineHeight: 1.4 }}>
                {commentary[0] && <div>🔊 {commentary[0]}</div>}
              </div>
            </div>
          </div>

          {/* ── POOLS + ACTIVITY ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            {/* Multi-market pool overview */}
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--accent)", marginBottom: 8 }}>📊 Live Pool Stakes</div>
              {["over_runs", "wicket", "batsman_boundary"].map(mkt => {
                const pool = totalPool(mkt);
                const stakes = poolStakes[mkt] || {};
                const keys = Object.keys(stakes);
                const firstKey = keys[0];
                const firstVal = stakes[firstKey] || 0;
                const pct = pool > 0 ? (firstVal / pool) * 100 : 50;
                return (
                  <div key={mkt} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginBottom: 2 }}>{MARKETS[mkt]?.icon} {MARKETS[mkt]?.label} <span style={{ color: "var(--accent)", fontWeight: 700 }}>{pool} PB</span></div>
                    <div style={{ height: 5, borderRadius: 3, overflow: "hidden", background: "rgba(255,255,255,0.1)", display: "flex" }}>
                      <div style={{ width: `${pct}%`, background: "var(--accent)", transition: "width 0.5s ease" }} />
                      <div style={{ flex: 1, background: "rgba(255,255,255,0.25)" }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bot activity feed */}
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#2ecc71", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#2ecc71", animation: "blink 1s infinite" }} />
                ⚡ Live Wager Feed
              </div>
              <div style={{ flex: 1, overflowY: "auto", fontSize: 10, fontFamily: "monospace", display: "flex", flexDirection: "column", gap: 3 }} className="custom-scroll">
                {botBetsFeed.length === 0 ? (
                  <div style={{ color: "rgba(255,255,255,0.4)", fontStyle: "italic", textAlign: "center", paddingTop: 10 }}>Waiting for wagers...</div>
                ) : botBetsFeed.map((feed, i) => (
                  <div key={i} style={{ color: i === 0 ? "#2ecc71" : "rgba(255,255,255,0.55)", lineHeight: 1.3 }}>» {feed}</div>
                ))}
              </div>
            </div>
          </div>

          {/* ── MARKET TABS ── */}
          <div style={{ marginBottom: 16 }}>
            {/* Suspended overlay */}
            {isSuspended && (
              <div style={{ background: "rgba(239,1,7,0.08)", border: "1px solid rgba(239,1,7,0.3)", borderRadius: 10, padding: "8px 16px", marginBottom: 10, display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF0107", animation: "blink 0.5s infinite", display: "inline-block" }} />
                <span style={{ color: "#EF0107", fontWeight: 700, fontSize: 12, letterSpacing: "0.04em" }}>🔒 MARKETS SUSPENDED — DELIVERY IN PROGRESS</span>
              </div>
            )}

            {/* Tab selector */}
            <div style={{ display: "flex", gap: 4, marginBottom: 12, background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: 4 }}>
              {[
                { key: "ball", label: "⚡ Ball-by-Ball", count: 2 },
                { key: "over", label: "📊 Over Props", count: 3 },
                { key: "match", label: "🏆 Match Winner", count: 1 }
              ].map(tab => (
                <button key={tab.key} onClick={() => setActiveMarketTab(tab.key)} style={{ flex: 1, padding: "8px 4px", borderRadius: 8, border: "none", background: activeMarketTab === tab.key ? "var(--accent)" : "transparent", color: activeMarketTab === tab.key ? "#000" : "rgba(255,255,255,0.6)", fontWeight: activeMarketTab === tab.key ? 700 : 500, fontSize: 12, cursor: "pointer", transition: "all 0.2s" }}>
                  {tab.label}
                  <span style={{ fontSize: 9, marginLeft: 4, background: activeMarketTab === tab.key ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.1)", borderRadius: 99, padding: "1px 5px" }}>{tab.count}</span>
                </button>
              ))}
            </div>

            {/* Market cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {activeTabMarkets.map(mktKey => {
                const mkt = MARKETS[mktKey];
                const odds = computeOdds(mktKey);
                const pool = totalPool(mktKey);
                return (
                  <div key={mktKey} style={{ background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "12px 14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{mkt.icon} {mkt.label}</div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>{mkt.description}</div>
                      </div>
                      <div style={{ fontSize: 10, color: "var(--accent)", fontWeight: 600 }}>Pool: {pool} PB</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(mkt.outcomes.length, 3)}, 1fr)`, gap: 8 }}>
                      {mkt.outcomes.map(outcome => {
                        const isSelected = selectedMarket === mktKey && selectedOutcome === outcome.key;
                        const liveOdds = odds[outcome.key] || 1.85;
                        const flashKey = `${mktKey}.${outcome.key}`;
                        const flashDir = oddsFlash[flashKey];
                        const stakes = poolStakes[mktKey]?.[outcome.key] || 0;
                        const poolPct = pool > 0 ? Math.round((stakes / pool) * 100) : 0;
                        return (
                          <button
                            key={outcome.key}
                            disabled={isSuspended || balls >= 6}
                            onClick={() => selectOutcome(mktKey, outcome.key)}
                            style={{
                              padding: "10px 8px",
                              borderRadius: 10,
                              border: isSelected ? `2px solid ${outcome.color}` : "1px solid rgba(255,255,255,0.1)",
                              background: isSelected ? `${outcome.color}22` : "rgba(255,255,255,0.02)",
                              color: isSelected ? outcome.color : "#fff",
                              cursor: isSuspended || balls >= 6 ? "not-allowed" : "pointer",
                              opacity: isSuspended ? 0.5 : 1,
                              transition: "all 0.15s",
                              textAlign: "center",
                              position: "relative",
                              overflow: "hidden"
                            }}
                          >
                            {/* Pool bar at bottom */}
                            <div style={{ position: "absolute", bottom: 0, left: 0, width: `${poolPct}%`, height: 2, background: outcome.color, opacity: 0.6, transition: "width 0.5s ease" }} />
                            <div style={{ fontSize: 16, marginBottom: 2 }}>{outcome.emoji}</div>
                            <div style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2, marginBottom: 4 }}>{outcome.label}</div>
                            <div style={{
                              fontSize: 13, fontWeight: 900, fontFamily: "monospace",
                              color: flashDir === "up" ? "#2ecc71" : flashDir === "down" ? "#e74c3c" : (isSelected ? outcome.color : "var(--accent)"),
                              transition: "color 0.3s"
                            }}>
                              {liveOdds}x {flashDir === "up" ? "▲" : flashDir === "down" ? "▼" : ""}
                            </div>
                            <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>{poolPct}% of pool</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── BET SLIP ── */}
          {selectedOutcome && (
            <div style={{ background: "linear-gradient(135deg, rgba(29,158,117,0.12) 0%, rgba(29,158,117,0.03) 100%)", border: "1px solid rgba(29,158,117,0.4)", borderRadius: 12, padding: 14, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "var(--green)", letterSpacing: "0.06em" }}>LOCK IN ODDS</div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {MARKETS[selectedMarket]?.outcomes.find(o => o.key === selectedOutcome)?.label || selectedOutcome}
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 400, marginLeft: 6 }}>({MARKETS[selectedMarket]?.label})</span>
                  </div>
                </div>
                <div style={{ background: "rgba(29,158,117,0.2)", color: "var(--green)", fontWeight: 800, padding: "4px 10px", borderRadius: 6, fontSize: 13, border: "0.5px solid rgba(29,158,117,0.4)" }}>
                  {getOddsDisplay(selectedMarket, selectedOutcome)}x
                </div>
              </div>

              {countdown !== null ? (
                <div style={{ textAlign: "center", padding: "10px 0" }}>
                  <div style={{ fontSize: 13, color: "var(--accent)" }}>🚨 PLACING WAGER IN:</div>
                  <div style={{ fontSize: 40, fontWeight: 900, fontFamily: "monospace", color: "#EF0107" }}>{countdown}s</div>
                  <button onClick={cancelCountdown} style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.4)", textDecoration: "underline", fontSize: 12, cursor: "pointer" }}>✕ Cancel Bet</button>
                </div>
              ) : (
                <div>
                  {currentRoom ? (
                    <div style={{ padding: "10px 12px", background: "rgba(255,255,255,0.04)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>Fixed Room Stake:</span>
                      <strong style={{ fontSize: 16, color: "var(--accent)" }}>{currentRoom.entryFee} PB</strong>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.5)", marginBottom: 8, letterSpacing: "0.05em" }}>SELECT STAKE SIZE:</div>
                      <div style={{ display: "grid", gridTemplateColumns: `repeat(${slotTiers.length}, 1fr)`, gap: 8, marginBottom: 10 }}>
                        {slotTiers.map(tier => {
                          const isActive = parseInt(betAmount) === tier;
                          const canAfford = points >= tier;
                          return (
                            <button key={tier} type="button" disabled={!canAfford} onClick={() => { playSound("click"); setBetAmount(String(tier)); }} style={{ padding: "10px 6px", borderRadius: 8, fontWeight: "bold", fontSize: 13, cursor: canAfford ? "pointer" : "not-allowed", background: isActive ? "var(--accent)" : canAfford ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.02)", border: isActive ? "1px solid var(--accent)" : "1px solid rgba(255,255,255,0.1)", color: isActive ? "#000" : canAfford ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.25)", textAlign: "center" }}>
                              <div>{tier}pts</div>
                              <div style={{ fontSize: 9, opacity: 0.6, marginTop: 2 }}>{canAfford ? "OK" : "Low"}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={startBetCountdown}
                    disabled={isPlacingBet || (!currentRoom && (!betAmount || parseInt(betAmount) <= 0 || parseInt(betAmount) > points))}
                    style={{ width: "100%", background: "var(--accent)", color: "#000", border: "none", borderRadius: 8, padding: 12, fontWeight: 700, cursor: "pointer", fontSize: 14, transition: "opacity 0.2s" }}
                  >
                    {isPlacingBet ? "Locking..." : `🔒 Lock Wager (${currentRoom ? currentRoom.entryFee : betAmount || "?"} PB)`}
                  </button>
                  {(betAmount || currentRoom) && (
                    <div style={{ fontSize: 11, color: "var(--green)", marginTop: 6 }}>
                      Est. Payout: <strong>{Math.floor((currentRoom ? currentRoom.entryFee : parseInt(betAmount) || 0) * getOddsDisplay(selectedMarket, selectedOutcome))} PB</strong> (Pari-Mutuel, 10% rake)
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", fontStyle: "italic", marginTop: 4 }}>
                    ⚠️ Pari-mutuel odds shift in real time. No-contest pools refund 97.5% (2.5% house fee). Cash out available before suspension.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ACTIVE & SETTLED BETS ── */}
          <div style={{ background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(255,255,255,0.05)", borderRadius: 12, padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: "0.04em", marginBottom: 10, textTransform: "uppercase" }}>⚡ Your In-Play Bets — Over {overs + 1} ({activeLiveBets.length})</div>
            {activeLiveBets.length === 0 ? (
              <div style={{ textAlign: "center", padding: "16px 0", fontSize: 12, color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>No active bets placed for the current over yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {activeLiveBets.map((b, idx) => {
                  const liveOdds = computeOdds(b.marketType)[b.choice] || 1.85;
                  const outcome = MARKETS[b.marketType]?.outcomes.find(o => o.key === b.choice);
                  return (
                    <div key={idx} style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", padding: "10px 14px", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{ fontSize: 18 }}>{outcome?.emoji || "⏳"}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{outcome?.label || b.choice} <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>({(MARKETS[b.marketType]?.label || b.marketType).replace(/_/g, " ")})</span></div>
                          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Stake: {b.amount} PB • Over {b.over}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: 12 }}>{liveOdds}x LIVE</div>
                          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)" }}>Est. {Math.floor(b.amount * liveOdds)} PB</div>
                        </div>
                        {!isSuspended && (
                          <button onClick={() => handleCashOut(b)} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid rgba(255,193,7,0.4)", background: "rgba(255,193,7,0.08)", color: "#ffc107", fontWeight: 700, cursor: "pointer", fontSize: 10, whiteSpace: "nowrap" }}>
                            💰 Cash Out<br /><span style={{ fontWeight: 400 }}>~{Math.floor(b.amount * (b.odds / liveOdds) * 0.95)} PB</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {betHistory.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: "0.04em", marginBottom: 8, textTransform: "uppercase" }}>📜 Settled In-Play History ({betHistory.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {betHistory.slice(0, 10).map((b, idx) => {
                    const isWon = b.status === "won";
                    const isRefunded = b.status === "refunded";
                    const isCashedOut = b.status === "cashed_out";
                    const outcome = MARKETS[b.marketType]?.outcomes.find(o => o.key === b.choice);
                    const borderColor = isWon ? "#2ecc71" : isCashedOut ? "#ffc107" : isRefunded ? "#3498db" : "#e74c3c";
                    return (
                      <div key={idx} style={{ background: `rgba(${isWon ? "46,204,113" : isCashedOut ? "255,193,7" : isRefunded ? "52,152,219" : "231,76,60"},0.05)`, border: `0.5px solid rgba(${isWon ? "46,204,113" : isCashedOut ? "255,193,7" : isRefunded ? "52,152,219" : "231,76,60"},0.2)`, borderLeft: `3px solid ${borderColor}`, padding: "8px 12px", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 12 }}>{outcome?.emoji || ""} {outcome?.label || b.choice} <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Over {b.over}</span></div>
                          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Staked {b.amount} PB</div>
                        </div>
                        <div style={{ textAlign: "right", fontWeight: 700, color: borderColor, fontSize: 12 }}>
                          {isWon ? `+${b.winAmount} WON` : isCashedOut ? `+${b.winAmount} CASHED` : isRefunded ? "±REFUND" : `-${b.amount} LOST`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
